import { isRetryableTelemetryStatus } from '../../../isRetryableTelemetryStatus/isRetryableTelemetryStatus';
import {
  RateLimitedLogger as DefaultRateLimitedLogger,
  type IRateLimitedLogger
} from '../../../RateLimitedLogger/RateLimitedLogger';
import { TelemetryTransportError } from '../../../TelemetryTransportError/TelemetryTransportError';
import type { AgentContext, LogEvent, MetricEvent } from '../../../types/types';
import type { ITelemetryTransport } from '../../TelemetryAgent';

/** Wire discriminator (`ITelemetryTransport`) → the unauthenticated beacon path on `services/metrics`. */
const BEACON_PATHS: Record<'/v1/logs' | '/v1/metrics', '/v1/beacon/logs' | '/v1/beacon/metrics'> = {
  '/v1/logs': '/v1/beacon/logs',
  '/v1/metrics': '/v1/beacon/metrics'
};

/** `navigator.sendBeacon` surface — the queued-request path that survives `pagehide`. */
export type SendBeaconFn = (url: string, data: Blob) => boolean;

export interface SiteBeaconTransportOptions {
  /** Ingest origin (no trailing path), eg: `https://metrics.blufin.org/telemetry`. */
  baseUrl: string;
  /** Override the `fetch(url, { keepalive: true })` fallback (test-only / non-browser). */
  fetchFn?: typeof fetch;
  rateLimitedLogger?: IRateLimitedLogger;
  /** Override `navigator.sendBeacon` (test-only / non-browser). `null` forces the fetch fallback. */
  sendBeacon?: SendBeaconFn | null;
}

export interface ISiteBeaconTransport extends ITelemetryTransport {}

/**
 * Browser telemetry transport for the anonymous site beacon path. Each batch is POSTed to the
 * unauthenticated ingest pair (`/v1/beacon/metrics`, `/v1/beacon/logs`) via `navigator.sendBeacon` —
 * the queued, unload-surviving path — falling back to `fetch(url, { keepalive: true })` when
 * `sendBeacon` is unavailable or refuses the payload (returns `false`). Batching, size caps, and
 * retry are owned by `TelemetryAgent`; this transport does one last-mile flush per `send` and throws
 * a `TelemetryTransportError` for the agent to classify retry-vs-drop. Never signs, never sets a
 * cookie — the site path is cookieless.
 */
export class SiteBeaconTransport implements ISiteBeaconTransport {
  private readonly _baseUrl: string;
  private readonly _fetchFn: typeof fetch | undefined;
  private readonly _rateLimitedLogger: IRateLimitedLogger;
  private readonly _sendBeacon: SendBeaconFn | null;

  /**
   * @param options - Construction options.
   * @param options.baseUrl - Ingest origin (no trailing path); the beacon subpath is appended per endpoint.
   * @param options.fetchFn - Override for the `fetch(keepalive)` fallback (test-only / non-browser).
   * @param options.rateLimitedLogger - Override for the rate-limited warn helper.
   * @param options.sendBeacon - Override for `navigator.sendBeacon`; `null` forces the fetch fallback.
   */
  constructor(options: SiteBeaconTransportOptions) {
    this._baseUrl = options.baseUrl.replace(/\/+$/, '');
    this._fetchFn =
      options.fetchFn ?? (typeof globalThis.fetch === 'function' ? globalThis.fetch.bind(globalThis) : undefined);
    this._rateLimitedLogger = options.rateLimitedLogger ?? DefaultRateLimitedLogger;
    this._sendBeacon =
      options.sendBeacon !== undefined
        ? options.sendBeacon
        : typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
          ? navigator.sendBeacon.bind(navigator)
          : null;
  }

  /** @inheritdoc */
  send(endpoint: '/v1/logs', body: { context: AgentContext; events: LogEvent[] }): Promise<void>;
  /** @inheritdoc */
  send(endpoint: '/v1/metrics', body: { context: AgentContext; events: MetricEvent[] }): Promise<void>;
  /**
   * Flush one batch to the beacon ingest pair. Tries `navigator.sendBeacon` first (survives `pagehide`),
   * falling back to `fetch(url, { keepalive: true })` when it is missing or refuses the payload.
   * @param endpoint - Wire discriminator (`/v1/logs` or `/v1/metrics`); routed to `/v1/beacon/*`.
   * @param body - The agent context + events payload.
   * @param body.context - The agent context snapshot sent alongside the batch.
   * @param body.events - The batch of log or metric events.
   */
  async send(
    endpoint: '/v1/logs' | '/v1/metrics',
    body: { context: AgentContext; events: LogEvent[] | MetricEvent[] }
  ): Promise<void> {
    const url = `${this._baseUrl}${BEACON_PATHS[endpoint]}`;
    const payload = JSON.stringify(body);
    if (this._trySendBeacon(url, payload)) return;
    await this._fetchFallback(url, payload);
  }

  /**
   * Fire the `fetch(keepalive)` fallback, throwing a classified `TelemetryTransportError` on failure.
   * @param url - The fully-qualified beacon URL.
   * @param payload - The serialized `{ context, events }` body.
   */
  private async _fetchFallback(url: string, payload: string): Promise<void> {
    if (!this._fetchFn) {
      throw new TelemetryTransportError('SiteBeaconTransport: no sendBeacon and no fetch available.', {
        retryable: false
      });
    }
    let statusCode: number | undefined;
    try {
      const response = await this._fetchFn(url, {
        body: payload,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        method: 'POST'
      });
      statusCode = response.status;
      if (response.ok) return;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : typeof err === 'string' ? err : JSON.stringify(err);
      throw new TelemetryTransportError(message, { retryable: true });
    }
    throw new TelemetryTransportError(`SiteBeaconTransport: beacon POST failed with status ${statusCode}.`, {
      retryable: isRetryableTelemetryStatus(statusCode ?? null),
      statusCode
    });
  }

  /**
   * Attempt the `navigator.sendBeacon` path. Returns `false` (so the caller falls back to `fetch`)
   * when `sendBeacon` is absent, throws, or refuses the payload (browser queue full / oversized).
   * @param url - The fully-qualified beacon URL.
   * @param payload - The serialized `{ context, events }` body.
   */
  private _trySendBeacon(url: string, payload: string): boolean {
    if (!this._sendBeacon) return false;
    try {
      const blob = new Blob([payload], { type: 'application/json' });
      const queued = this._sendBeacon(url, blob);
      if (!queued) {
        this._rateLimitedLogger.warn(
          'site-beacon:queue-refused',
          '[SiteBeaconTransport] sendBeacon refused the payload; falling back to fetch(keepalive).'
        );
      }
      return queued;
    } catch {
      this._rateLimitedLogger.warn(
        'site-beacon:send-threw',
        '[SiteBeaconTransport] sendBeacon threw; falling back to fetch(keepalive).'
      );
      return false;
    }
  }
}
